import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../../config';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { UpgradeModal } from '../../../components/UpgradeModal';

interface Trip {
  id: string;
  title: string;
}

interface AddPinDialogProps {
  open: boolean;
  latitude: number;
  longitude: number;
  onClose: () => void;
  onSuccess: () => void;
  initialTitle?: string;
}

/**
 * Dialog for creating a new pin (check-in) with optional trip linking and photo upload.
 */
export function AddPinDialog({ open, latitude, longitude, onClose, onSuccess, initialTitle }: AddPinDialogProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialTitle || '');
  const [description, setDescription] = useState('');
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [tripId, setTripId] = useState('');
  const [trips, setTrips] = useState<Trip[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [placeName, setPlaceName] = useState('');
  const [pinCity, setPinCity] = useState('');
  const [pinCountryCode, setPinCountryCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState<{ plan: string; photosUsed: number; photoQuota: number }>({ plan: 'free', photosUsed: 0, photoQuota: 100 });

  // Reverse geocode on open
  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle || '');
    setDescription('');
    setVisitDate(new Date().toISOString().split('T')[0]);
    setTripId('');
    setPhotos([]);
    setPhotoPreviews([]);
    setUploadProgress(0);
    setPinCity('');
    setPinCountryCode('');

    const token = import.meta.env.VITE_MAPBOX_TOKEN || '';
    // Reverse geocode to get address, city, country (POI name comes from map click via initialTitle)
    fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.features && data.features.length > 0) {
          // Find the most specific feature (address > poi > place)
          const addressFeature = data.features.find((f: any) => f.id?.startsWith('address'));
          const poiFeature = data.features.find((f: any) => f.id?.startsWith('poi'));
          const placeFeature = data.features.find((f: any) => f.id?.startsWith('place'));
          // Use address for placeName display, or first feature
          const displayFeature = addressFeature || data.features[0];
          setPlaceName(displayFeature.place_name || '');

          // Extract city and country from any feature's context
          const anyFeature = addressFeature || poiFeature || placeFeature || data.features[0];
          if (anyFeature.context) {
            const countryCtx = anyFeature.context.find((c: any) => c.id && c.id.startsWith('country'));
            const placeCtx = anyFeature.context.find((c: any) => c.id && c.id.startsWith('place'));
            if (countryCtx && countryCtx.short_code) setPinCountryCode(countryCtx.short_code.toUpperCase());
            if (placeCtx) setPinCity(placeCtx.text || '');
          }
          if (!pinCity) {
            const placeParts = (displayFeature.place_name || '').split(', ');
            if (placeParts.length >= 3) setPinCity(placeParts[placeParts.length - 3]);
            else if (placeParts.length >= 2) setPinCity(placeParts[placeParts.length - 2]);
          }
          // Only set title from geocode if no POI title was provided from map click
          if (!initialTitle) {
            // Prefer POI name from geocode results if available
            if (poiFeature) {
              setTitle(poiFeature.text || '');
            } else if (addressFeature) {
              setTitle(addressFeature.text || '');
            } else {
              setTitle(data.features[0].text || '');
            }
          }
        }
      })
      .catch(() => {});

    // Fetch user trips
    const authToken = localStorage.getItem('accessToken');
    fetch(`${API_BASE_URL}/api/v1/trips`, { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} })
      .then(r => r.json())
      .then(data => {
        const tripList = Array.isArray(data.trips) ? data.trips : Array.isArray(data) ? data : [];
        setTrips(tripList);
      })
      .catch(() => setTrips([]));
  }, [open, latitude, longitude, initialTitle]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setPhotos(prev => [...prev, ...files]);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotoPreviews(prev => [...prev, ev.target?.result as string]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const removePhoto = useCallback((index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const authToken = localStorage.getItem('accessToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      // Step 1: Create pin
      const pinRes = await fetch(`${API_BASE_URL}/api/v1/locations/pins`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tripId: tripId || undefined,
          latitude,
          longitude,
          title: title || undefined,
          description: description || undefined,
          visitDate: visitDate || undefined,
          city: pinCity || undefined,
          countryCode: pinCountryCode || undefined,
        }),
      });

      if (!pinRes.ok) {
        throw new Error('Failed to create pin');
      }

      const pin = await pinRes.json();
      let photosUploaded = 0;

      // Step 2: Upload photos one by one
      if (photos.length > 0) {
        setUploading(true);
        for (let i = 0; i < photos.length; i++) {
          setUploadProgress(((i) / photos.length) * 100);
          const formData = new FormData();
          formData.append('file', photos[i]);
          formData.append('pinId', pin.id);
          if (tripId) formData.append('tripId', tripId);

          const uploadRes = await fetch(`${API_BASE_URL}/api/v1/photos/upload`, {
            method: 'POST',
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
            body: formData,
          });
          if (uploadRes.status === 403) {
            const statusRes = await fetch(`${API_BASE_URL}/api/v1/billing/status`, { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} });
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              setQuotaInfo({ plan: statusData.plan, photosUsed: statusData.photosUsed, photoQuota: statusData.photoQuota });
            }
            setShowUpgrade(true);
            setUploading(false);
            setSubmitting(false);
            // Rollback: delete pin if no photos uploaded
            if (photosUploaded === 0) {
              await fetch(`${API_BASE_URL}/api/v1/locations/pins/${pin.id}`, { method: 'DELETE', headers });
            }
            return;
          }
          if (uploadRes.ok) photosUploaded++;
        }
        setUploadProgress(100);
        setUploading(false);
      }

      // Rollback: if no photos were uploaded successfully, delete the pin
      if (photosUploaded === 0 && photos.length > 0) {
        await fetch(`${API_BASE_URL}/api/v1/locations/pins/${pin.id}`, { method: 'DELETE', headers });
        throw new Error('No photos uploaded successfully');
      }

      // Check trophies after pin creation
      fetch(`${API_BASE_URL}/api/v1/trophies/check`, { method: 'POST', headers: authToken ? { Authorization: 'Bearer ' + authToken } : {} }).catch(() => {});
      onSuccess();
      onClose();
    } catch (err) {
      console.error('[AddPinDialog] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-container': { justifyContent: 'flex-end', alignItems: 'flex-start', pt: 8 }, '& .MuiDialog-paper': { m: 0, mr: 2, mt: 2, maxHeight: 'calc(100vh - 160px)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocationOnIcon color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>{t('map.checkIn')}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {placeName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {placeName}
          </Typography>
        )}

        <TextField
          label={t("map.title")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          label={t("map.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        <TextField
          label={t("map.visitDate")}
          type="date"
          value={visitDate}
          onChange={(e) => setVisitDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t("map.linkTrip")}</InputLabel>
          <Select
            value={tripId}
            onChange={(e) => setTripId(e.target.value)}
            label={t("map.linkTrip")}
          >
            <MenuItem value="">{t('map.noTrip')}</MenuItem>
            {trips.map((trip) => (
              <MenuItem key={trip.id} value={trip.id}>{trip.title}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Photo upload */}
        <Box
          sx={{
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.50' },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <AddPhotoAlternateIcon sx={{ fontSize: 40, color: 'grey.500' }} />
          <Typography variant="body2" color="text.secondary">
            {t("map.addPhotos")}
          </Typography>
          <Typography variant="caption" color="error">
            {t("map.photoRequired")}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </Box>

        {/* Photo previews */}
        {photoPreviews.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {photoPreviews.map((preview, idx) => (
                <Box key={idx} sx={{ position: 'relative', width: 80, height: 80 }}>
                  <img
                    src={preview}
                    alt={`Preview ${idx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removePhoto(idx)}
                    sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', width: 20, height: 20, '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button
              size="small"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ mt: 1 }}
            >
              {t("map.addMorePhotos")}
            </Button>
          </Box>
        )}

        {/* Upload progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary">
              {t("map.uploadingPhotos")} {Math.round(uploadProgress)}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || !title || photos.length === 0}>
          {submitting ? t('map.saving') : t('map.saveCheckin')}
        </Button>
      </DialogActions>
    </Dialog>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentPlan={quotaInfo.plan}
        photosUsed={quotaInfo.photosUsed}
        photoQuota={quotaInfo.photoQuota}
      />
    </>
  );
}